import formatterMonetaryValue from '@/app/utils/numberUtils';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  type SxProps,
  type Theme,
} from '@mui/material';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

const emptyStateCellSx: SxProps<Theme> = {
  textAlign: 'center',
};

export interface ExtraHeaderContentFields {
  system: string;
  erp: string;
}

export interface ExtraHeaderContent {
  systemValue: number;
  erpValue: number;
  diffValue?: number;
}

export interface Column {
  key: string;
  label: string | ReactNode;
  align?: 'left' | 'center' | 'right';
  width?: string;
  sticky?: 'left' | 'right';
  stickyOffset?: string;
  extraHeaderContent?: ExtraHeaderContent;
  extraHeaderContentFields?: ExtraHeaderContentFields;
}

interface ExpandableScrollDataTableProps<T> {
  columns: Column[];
  data: T[];
  renderRow: (item: T, index: number) => ReactNode;
  emptyMessage: string;
  getRowKey: (item: T, index: number) => string | number;
  maxHeight?: string;
  width?: string;
  stickyHeader?: boolean;
  size?: 'small' | 'medium';
}

const getHeaderCellSx = (column: Column): SxProps<Theme> => {
  const baseStyles: SxProps<Theme> = {
    fontSize: 'body2.fontSize',
    fontWeight: 'bold',
    backgroundColor: 'background.paper',
  };

  if (column.sticky) {
    return {
      ...baseStyles,
      position: 'sticky' as const,
      zIndex: column.sticky === 'left' ? 4 : 3,
      backgroundColor: 'background.paper',
      boxShadow: column.sticky === 'left' ? '2px 0 4px rgba(0, 0, 0, 0.05)' : '-2px 0 4px rgba(0, 0, 0, 0.05)',
      [column.sticky]: column.stickyOffset || 0,
    };
  }

  return baseStyles;
};

export function ErpValuePresentation(extraHeader: ExtraHeaderContent) {
  const { diffValue, erpValue, systemValue } = extraHeader;
  if (diffValue === 0) {
    return (
      <Typography
        variant="caption"
        sx={{
          fontSize: '0.65rem',
          textAlign: 'right',
          fontWeight: 400,
          color: erpValue === systemValue ? 'text.primary' : 'error.main',
        }}
      >
        {formatterMonetaryValue(erpValue || 0)}
      </Typography>
    );
  }
  return (
    <Tooltip title={formatterMonetaryValue(diffValue || 0)}>
      <Typography
        variant="caption"
        sx={{
          fontSize: '0.65rem',
          textAlign: 'right',
          fontWeight: 400,
          color: erpValue === systemValue ? 'text.primary' : 'error.main',
        }}
      >
        {formatterMonetaryValue(erpValue || 0)}
      </Typography>
    </Tooltip>
  );
}

const renderFormattedHeaderCell = (column: Column, systemText: string, erpText: string) => {
  if (column.extraHeaderContent == null) {
    return (
      <TableCell key={column.key} sx={getHeaderCellSx(column)} align={column.align} width={column.width}>
        {column.label}
      </TableCell>
    );
  }
  return (
    <TableCell key={column.key} sx={getHeaderCellSx(column)} align={column.align} width={column.width}>
      <Box display="flex" flexDirection="column" gap={0.5} alignItems="center">
        <Typography fontWeight={600} sx={{ fontSize: '0.9rem' }}>
          {column.label}
        </Typography>
        <Box display="grid" gridTemplateColumns="auto 1fr" gap={0.5} alignItems="center">
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
            {systemText}
          </Typography>
          <Typography variant="caption" sx={{ fontSize: '0.65rem', textAlign: 'right', fontWeight: 400 }}>
            {formatterMonetaryValue(column.extraHeaderContent?.systemValue || 0)}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
            {erpText.toUpperCase()}
          </Typography>

          {ErpValuePresentation(column.extraHeaderContent)}
        </Box>
      </Box>
    </TableCell>
  );
};

export const bodyCellSx: SxProps<Theme> = {
  fontSize: 'body2.fontSize',
  backgroundColor: 'background.paper',
};

export const headerCellSx: SxProps<Theme> = {
  fontSize: 'body2.fontSize',
  fontWeight: 'bold',
  backgroundColor: 'background.paper',
};

export const getStickyBodyCellSx = (column: Column): SxProps<Theme> => {
  if (!column.sticky) return bodyCellSx;

  return {
    ...bodyCellSx,
    position: 'sticky' as const,
    zIndex: column.sticky === 'left' ? 4 : 3,
    backgroundColor: 'background.paper',
    boxShadow: column.sticky === 'left' ? '2px 0 4px rgba(0, 0, 0, 0.05)' : '-2px 0 4px rgba(0, 0, 0, 0.05)',
    [column.sticky]: column.stickyOffset || 0,
  };
};

export const mainRowSx: SxProps<Theme> = {
  '& > *': { borderBottom: 'unset' },
  backgroundColor: 'grey.100',
};

export default function ExpandableScrollDataTable<T>({
  columns,
  data,
  renderRow,
  emptyMessage,
  getRowKey,
  maxHeight = '40rem',
  width = '51rem',
  stickyHeader = true,
  size = 'small',
}: ExpandableScrollDataTableProps<T>) {
  const { t } = useTranslation(['humanResource', 'common']);
  return (
    <Box sx={{ width: '100%', overflowX: 'auto' }}>
      <TableContainer
        sx={{
          height: maxHeight,
          width,
        }}
        component={Paper}
      >
        <Table stickyHeader={stickyHeader} size={size}>
          <TableHead>
            <TableRow>
              {columns.map((column) => renderFormattedHeaderCell(column, t('common:system'), t('common:erp')))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((item, index) => renderRow(item, index))}
            {data.length === 0 && (
              <TableRow>
                <TableCell sx={emptyStateCellSx} colSpan={columns.length}>
                  <Typography variant="subtitle2">{emptyMessage}</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
